import { BuyerUI } from '@choco-admin/types';
import { useCallback, useState, useMemo, useRef, useEffect } from 'react';

import {
  type BuyersPageSearchBuyersQuery,
  useBuyersPageSearchBuyersLazyQuery,
} from '../../../generated';
import { mapToBuyerUIList, type GeneratedBuyerFragment } from '../utils';

export type CaptureExceptionFunction = (error: Error) => void;

export type SearchBuyersParams = {
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  filter?: {
    isTest?: boolean;
    status?: string;
  };
};

export type UseSearchBuyersResponse = {
  buyers: BuyerUI[];
  totalBuyers: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  hasNextPage: boolean;
  currentPage: number;
  totalPages: number;
  searchBuyers: (params: SearchBuyersParams) => Promise<void>;
  refetch: () => Promise<void>;
};

export type UseSearchBuyersConfig = {
  initialPageSize: number;
  captureException?: CaptureExceptionFunction;
};

type BuyerSearchResult = BuyersPageSearchBuyersQuery['search'];

interface SearchHit {
  entity: GeneratedBuyerFragment;
}

interface SearchResultData {
  hits?: SearchHit[] | null;
  total?: number | null;
}

const mapBuyers = (
  result: BuyerSearchResult | null | undefined
): { buyers: BuyerUI[]; total: number } => {
  if (!result) {
    return { buyers: [], total: 0 };
  }

  const searchResult = result as SearchResultData;
  const { hits, total } = searchResult;

  try {
    const buyerEntities = hits?.map((hit) => hit.entity) || [];
    const buyers = mapToBuyerUIList(buyerEntities);
    return { buyers, total: total || 0 };
  } catch (error) {
    // Return empty result on mapping error
    return { buyers: [], total: 0 };
  }
};

export const useSearchBuyers = (
  config: UseSearchBuyersConfig
): UseSearchBuyersResponse => {
  const { captureException, initialPageSize } = config;
  const [lastParams, setLastParams] = useState<SearchBuyersParams | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Last non-zero total for the current search term. Page responses can omit
  // or zero the total, which used to make "Showing X of Y buyers" (and the
  // page count) lose Y after any pagination — keep the last-known value
  // instead (#109).
  const [lastKnownTotal, setLastKnownTotal] = useState(0);
  const lastSearchTermRef = useRef<string>('');

  const [executeQuery, { data, loading, error }] =
    useBuyersPageSearchBuyersLazyQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    });

  const searchBuyers = useCallback(
    async (params: SearchBuyersParams) => {
      try {
        setLastParams(params);
        const page = params.page || 1;
        const pageSize = params.pageSize || initialPageSize;
        setCurrentPage(page);

        // A new search term starts a new result set — forget the previous
        // total so a legitimate zero-result search shows 0 (#109).
        const searchTerm = params.searchTerm || '';
        if (searchTerm !== lastSearchTermRef.current) {
          lastSearchTermRef.current = searchTerm;
          setLastKnownTotal(0);
        }

        await executeQuery({
          variables: {
            searchTerm: params.searchTerm || '',
            size: pageSize,
            offset: (page - 1) * pageSize,
            filter: params.filter
              ? {
                  buyer: {
                    isPuppy: params.filter.isTest, // Map isTest to isPuppy for filtering
                  },
                }
              : undefined,
          },
        });
      } catch (err) {
        captureException?.(err as Error);
        throw err;
      }
    },
    [executeQuery, captureException, initialPageSize]
  );

  const refetch = useCallback(async () => {
    if (lastParams) {
      await searchBuyers(lastParams);
    }
  }, [lastParams, searchBuyers]);

  const { buyers, total } = useMemo(
    () => mapBuyers(data?.search),
    [data?.search]
  );

  useEffect(() => {
    if (total > 0) {
      setLastKnownTotal(total);
    }
  }, [total]);

  // Fall back to the last-known total when the current page response
  // omits/zeroes it (#109).
  const effectiveTotal = total > 0 ? total : lastKnownTotal;

  const totalPages = useMemo(() => {
    if (!effectiveTotal) return 0;
    const pageSize = lastParams?.pageSize ?? initialPageSize;
    return Math.ceil(effectiveTotal / pageSize);
  }, [effectiveTotal, lastParams?.pageSize, initialPageSize]);

  const hasNextPage = useMemo(() => {
    return currentPage < totalPages;
  }, [currentPage, totalPages]);

  return useMemo(
    () => ({
      buyers: buyers || [], // Ensure we always return an array
      totalBuyers: effectiveTotal,
      isLoading: loading,
      isFetching: loading,
      error: error || null,
      hasNextPage,
      currentPage,
      totalPages,
      searchBuyers,
      refetch,
    }),
    [
      buyers,
      effectiveTotal,
      loading,
      error,
      hasNextPage,
      currentPage,
      totalPages,
      searchBuyers,
      refetch,
    ]
  );
};
